import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  reactive,
  ref,
  shallowRef,
  toRaw,
  unref,
  watch,
  watchEffect,
} from 'vue'
import { Empty, Tree } from 'ant-design-vue'
import { difference, get, omit } from 'lodash-es'
import { useContextMenu } from '@tav-ui/hooks/web/useContextMenu'
import { filter, treeToList } from '@tav-ui/utils/helper/treeHelper'
import { extendSlots, getSlot } from '@tav-ui/utils/helper/tsxHelper'
import { isArray, isBoolean, isEmpty, isFunction } from '@tav-ui/utils/is'
import ScrollContainer from '@tav-ui/components/container-scroll'
import { treeProps } from './props'
import TreeHeader from './TreeHeader.vue'
import { TreeIcon } from './TreeIcon'
import { useTree } from './useTree'
import type {
  CheckEvent,
  CheckKeys,
  ContextMenuItem,
  FieldNames,
  Keys,
  Recordable,
  TreeActionType,
  TreeItem,
} from './types'
import type { CSSProperties } from 'vue'
// import { CreateContextOptions } from "@casta-fe-playground/components/ContextMenu";
type contextMenuOptions = {
  event: any
  items?: ContextMenuItem[]
}
interface State {
  expandedKeys: Keys
  selectedKeys: Keys
  checkedKeys: CheckKeys
  checkStrictly: boolean
}
export default defineComponent({
  name: 'TaTree',
  inheritAttrs: false,
  props: treeProps,
  emits: [
    'update:expandedKeys',
    'update:selectedKeys',
    'update:checkedKeys',
    'change',
    'check',
    'update:searchValue',
  ],
  setup(props, { attrs, slots, emit, expose }) {
    const state = reactive<State>({
      checkStrictly: props.checkStrictly,
      expandedKeys: props.expandedKeys || [],
      selectedKeys: props.selectedKeys || [],
      checkedKeys: props.checkedKeys || [],
    })

    const searchState = reactive({
      startSearch: false,
      searchText: '',
      searchData: [] as TreeItem[],
      prevCheckedKeys: [] as string[],
    })

    const treeDataRef = ref<TreeItem[]>([])

    const [createContextMenu] = useContextMenu()
    const prefixCls = 'ta-basic-tree'

    const getFieldNames = computed((): Required<FieldNames> => {
      const { fieldNames } = props
      return {
        children: 'children',
        title: 'title',
        key: fieldNames?.id ? fieldNames?.id : fieldNames?.key ? fieldNames?.key : 'key',
        id: fieldNames?.id ?? 'id',
        pid: fieldNames?.pid ?? 'pid',
        ...omit(fieldNames, ['key', 'id', 'pid']),
      }
    })

    const getBindValues = computed(() => {
      const propsData = {
        blockNode: true,
        ...attrs,
        ...props,
        expandedKeys: state.expandedKeys,
        selectedKeys: state.selectedKeys,
        checkedKeys: state.checkedKeys,
        checkStrictly: state.checkStrictly,
        fieldNames: unref(getFieldNames),
        'onUpdate:expandedKeys': (v: Keys) => {
          state.expandedKeys = v
          emit('update:expandedKeys', v)
        },
        'onUpdate:selectedKeys': (v: Keys) => {
          state.selectedKeys = v
          emit('update:selectedKeys', v)
        },
        onCheck: (v: CheckKeys, e: CheckEvent) => {
          let currentValue = toRaw(state.checkedKeys) as Keys
          if (isArray(currentValue) && searchState.startSearch) {
            currentValue = [...currentValue, ...searchState.prevCheckedKeys]
            const { key: keyField } = unref(getFieldNames)
            const target = e.node.$attrs ? e.node.$attrs : e.node
            currentValue = difference(currentValue, getChildrenKeys(target[keyField]))
            if (e.checked && Array.isArray(currentValue)) {
              currentValue.push(target[keyField] as string)
            }
            state.checkedKeys = [...new Set([...currentValue])]
          } else {
            state.checkedKeys = v
          }

          const rawVal = toRaw(state.checkedKeys)
          emit('update:checkedKeys', rawVal)
          emit('check', rawVal, e)
        },
        onRightClick: handleRightClick,
      }
      return omit(propsData, 'treeData', 'class')
    })

    const getTreeData = computed((): TreeItem[] =>
      searchState.startSearch ? searchState.searchData : unref(treeDataRef)
    )

    const getNotFound = computed((): boolean => {
      return !getTreeData.value || getTreeData.value.length === 0
    })

    const {
      deleteNodeByKey,
      insertNodeByKey,
      insertNodesByKey,
      filterByLevel,
      updateNodeByKey,
      getAllKeys,
      getChildrenKeys,
      getEnabledKeys,
    } = useTree(treeDataRef, getFieldNames)

    function getIcon(params: Recordable, icon?: string) {
      if (!icon) {
        if (props.renderIcon && isFunction(props.renderIcon)) {
          return props.renderIcon(params)
        }
      }
      return icon
    }

    async function handleRightClick({ event, node }: Recordable) {
      const { rightMenuList, beforeRightClick } = props
      const contextMenuOptions: contextMenuOptions = { event, items: [] }

      if (beforeRightClick && isFunction(beforeRightClick)) {
        const result = await beforeRightClick(node, event)
        if (Array.isArray(result)) {
          contextMenuOptions.items = result
        } else {
          Object.assign(contextMenuOptions, result)
        }
      } else {
        contextMenuOptions['items'] = rightMenuList
      }
      if (!contextMenuOptions.items?.length) return
      createContextMenu(contextMenuOptions)
    }

    function setExpandedKeys(keys: Keys) {
      state.expandedKeys = keys
    }

    function getExpandedKeys() {
      return state.expandedKeys
    }
    function setSelectedKeys(keys: Keys) {
      state.selectedKeys = keys
    }

    function getSelectedKeys() {
      return state.selectedKeys
    }

    function setCheckedKeys(keys: CheckKeys) {
      state.checkedKeys = keys
    }

    function getCheckedKeys() {
      return state.checkedKeys
    }

    function checkAll(checkAll: boolean) {
      state.checkedKeys = checkAll ? getEnabledKeys() : ([] as Keys)
    }

    function expandAll(expandAll: boolean) {
      state.expandedKeys = expandAll ? getAllKeys() : ([] as Keys)
    }

    function onStrictlyChange(strictly: boolean) {
      state.checkStrictly = strictly
    }

    watch(
      () => props.searchValue,
      (val) => {
        if (val !== searchState.searchText) {
          searchState.searchText = val
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => props.treeData,
      (val) => {
        if (val) {
          handleSearch(searchState.searchText)
        }
      }
    )

    const searchedKeys = shallowRef<string[]>([])
    async function handleSearch(searchValue: string) {
      if (searchValue !== searchState.searchText) searchState.searchText = searchValue
      emit('update:searchValue', searchValue)

      if (searchValue) {
        searchState.prevCheckedKeys = [...(state.checkedKeys as any)]
        const { filterFn /*checkable, expandOnSearch, checkOnSearch, selectedOnSearch*/ } =
          unref(props)
        searchState.startSearch = true
        await nextTick()
        const { title: titleField, key: keyField, pid: pidField } = unref(getFieldNames)
        const matchedKeys: string[] = []
        searchState.searchData = filter(
          unref(treeDataRef),
          (node) => {
            let result = false
            if (filterFn) {
              result = filterFn(searchValue, node, unref(getFieldNames))
            } else {
              result = node[titleField]?.includes(searchValue)
            }
            if (result) {
              matchedKeys.push(node[keyField])
            }
            return result
          },
          {
            id: keyField,
            pid: pidField,
          }
        )
        searchedKeys.value = treeToList(searchState.searchData).map((val: any) => {
          return val[keyField]
        })

        // if (expandOnSearch) {
        //   const expandKeys = treeToList(searchState.searchData).map((val: any) => {
        //     return val[keyField]
        //   })
        //   if (expandKeys && expandKeys.length) {
        //     setExpandedKeys(expandKeys)
        //   }
        // }

        // if (checkOnSearch && checkable && matchedKeys.length) {
        //   setCheckedKeys(matchedKeys)
        // }

        // if (selectedOnSearch && matchedKeys.length) {
        //   setSelectedKeys(matchedKeys)
        // }

        if (searchedKeys.value.length) {
          setExpandedKeys([...new Set([...state.expandedKeys, ...searchedKeys.value])])
        }
      } else {
        searchState.startSearch = false
        await nextTick()

        if (searchedKeys.value.length) {
          setExpandedKeys([...new Set([...state.expandedKeys, ...searchedKeys.value])])
        }
        searchedKeys.value = []
        searchState.prevCheckedKeys = []

        return
      }
    }

    function handleClickNode(key: string, children: TreeItem[]) {
      if (!props.clickRowToExpand || !children || children.length === 0) return
      if (!state.expandedKeys.includes(key)) {
        setExpandedKeys([...state.expandedKeys, key])
      } else {
        const keys = [...state.expandedKeys]
        const index = keys.findIndex((item) => item === key)
        if (index !== -1) {
          keys.splice(index, 1)
        }
        setExpandedKeys(keys)
      }
    }

    watchEffect(() => {
      treeDataRef.value = props.treeData as TreeItem[]
    })

    onMounted(() => {
      const level = parseInt(props.defaultExpandLevel.toString())
      if (level > 0) {
        state.expandedKeys = filterByLevel(level)
      } else if (props.defaultExpandAll) {
        expandAll(true)
      }
    })

    watchEffect(() => {
      state.expandedKeys = props.expandedKeys
    })

    watchEffect(() => {
      state.selectedKeys = props.selectedKeys
    })

    watchEffect(() => {
      state.checkedKeys = props.checkedKeys
    })

    watchEffect(() => {
      state.checkStrictly = props.checkStrictly
    })

    const instance: TreeActionType = {
      setExpandedKeys,
      getExpandedKeys,
      setSelectedKeys,
      getSelectedKeys,
      setCheckedKeys,
      getCheckedKeys,
      insertNodeByKey,
      insertNodesByKey,
      deleteNodeByKey,
      updateNodeByKey,
      checkAll,
      expandAll,
      filterByLevel: (level: number) => {
        state.expandedKeys = filterByLevel(level)
      },
      setSearchValue: (value: string) => {
        handleSearch(value)
      },
      getSearchValue: () => {
        return searchState.searchText
      },
    }

    expose(instance)

    function renderAction(node: TreeItem) {
      const { actionList } = props
      if (!actionList || actionList.length === 0) return
      return actionList.map((item, index) => {
        let nodeShow = true
        if (isFunction(item.show)) {
          nodeShow = item.show?.(node)
        } else if (isBoolean(item.show)) {
          nodeShow = item.show
        }

        if (!nodeShow) return null

        return (
          <span key={index} class={`${prefixCls}__action`}>
            {item.render(node)}
          </span>
        )
      })
    }

    function renderTreeNode({ data, level }: { data: TreeItem[] | undefined; level: number }) {
      return data && data.length > 0
        ? data.map((item) => {
            const { key: keyField, children: childrenField } = unref(getFieldNames)

            const propsData = omit(item, 'title')
            const children = get(item, childrenField) || []

            return (
              <Tree.TreeNode
                {...propsData}
                node={toRaw(item)}
                key={get(item, keyField)}
                class={`${
                  !item[childrenField!] || item[childrenField!].length === 0
                    ? 'level---1'
                    : `level--${level}`
                }`}
              >
                {{
                  default: () => renderTreeNode({ data: children, level: level + 1 }),
                }}
              </Tree.TreeNode>
            )
          })
        : []
    }

    function renderTreeNodeTitle({ data }: { data: TreeItem | undefined }) {
      if (!data) {
        return null
      }
      const searchText = searchState.searchText
      const { highlight } = unref(props)
      const { title: titleField, key: keyField, children: childrenField } = unref(getFieldNames)
      const icon = getIcon({ ...data }, data.icon)
      const title = get(data, titleField)

      const searchIdx = searchText ? title.indexOf(searchText) : -1
      const isHighlight =
        searchState.startSearch && !isEmpty(searchText) && highlight && searchIdx !== -1

      const titleDom = (
        <span class={`${prefixCls}-title__content ${prefixCls}-title__content--highlight`}>
          {isHighlight ? (
            <>
              <span>{title.substr(0, searchIdx)}</span>
              <span class="highlight-keyword">{searchText}</span>
              <span>{title.substr(searchIdx + (searchText as string).length)}</span>
            </>
          ) : (
            title
          )}
        </span>
      )

      return (
        <span
          class={`${prefixCls}-title pl-2`}
          onClick={handleClickNode.bind(null, data[keyField], data[childrenField])}
        >
          {icon && TreeIcon({ icon })}
          {titleDom}
          {/*{get(data, titleField)}*/}
          <span class={`${prefixCls}-title__actions`}>{renderAction({ ...data })}</span>
        </span>
      )
    }

    return () => {
      const { title, helpMessage, toolbar, search, checkable } = props
      const showTitle = title || toolbar || search || slots.headerTitle
      const scrollStyle: CSSProperties = { height: 'calc(100% - 38px)' }
      return (
        <div
          class={[
            prefixCls,
            'h-full',
            attrs.class,
            unref(getBindValues)?.inlineLayoutLevel !== undefined ? `inline-layout-level` : '',
          ]}
        >
          {showTitle && (
            <TreeHeader
              checkable={checkable}
              checkAll={checkAll}
              expandAll={expandAll}
              title={title}
              search={search}
              toolbar={toolbar}
              helpMessage={helpMessage}
              onStrictlyChange={onStrictlyChange}
              onSearch={handleSearch}
              searchText={searchState.searchText}
            >
              {extendSlots(slots)}
            </TreeHeader>
          )}
          <ScrollContainer style={scrollStyle} v-show={!unref(getNotFound)}>
            <Tree {...unref(getBindValues)} showIcon={false}>
              {{
                // switcherIcon: () => <DownOutlined />,
                default: () => renderTreeNode({ data: unref(getTreeData), level: 1 }),
                title: ({ data }: any) => {
                  return slots?.title && typeof slots?.title === 'string'
                    ? getSlot(slots, slots?.title, data)
                    : renderTreeNodeTitle({ data })
                },
                ...extendSlots(slots, ['title']),
              }}
            </Tree>
          </ScrollContainer>

          <Empty v-show={unref(getNotFound)} image={Empty.PRESENTED_IMAGE_SIMPLE} class="!mt-4" />
        </div>
      )
    }
  },
})
